import React from 'react';
import {
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const normalize = (text) => (typeof text === 'string' ? text.trim().toLowerCase() : '');
const stripDiacritics = (text) =>
    typeof text === 'string' ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';

const getOptionFromQuestion = (question, response) => {
    if (!question) return null;
    const refId = Number(response?.scaleOptionId);
    return (question.questionOptionScores || []).find((opt) => {
        const optId = opt.scaleOptionId || opt.scaleOption?.id || opt.id;
        return Number(optId) === refId;
    });
};

const deriveScoreFromResponse = (response, question) => {
    const directScore = toNumberOrNull(response?.score);
    if (directScore != null) return directScore;

    let label = response?.textValue;
    const option = getOptionFromQuestion(question, response);
    if (option) {
        const optionScore = toNumberOrNull(option?.score);
        if (optionScore != null) return optionScore;
        label = label || option?.scaleOption?.label || option?.label || option?.value;
    }

    const normalizedLabel = normalize(label);
    if (normalizedLabel && question) {
        const byLabel = (question.questionOptionScores || []).find((opt) => {
            const optLabel = normalize(opt?.scaleOption?.label || opt?.label || opt?.value);
            if (!optLabel) return false;
            if (optLabel === normalizedLabel) return true;

            return stripDiacritics(optLabel) === stripDiacritics(normalizedLabel);
        });
        const matchScore = toNumberOrNull(byLabel?.score);
        if (matchScore != null) return matchScore;
    }

    return null;
};

const computeZaritResult = (register, scale) => {
    if (!register || !scale) return null;
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    if (!responses.length || !questions.length) return null;

    const questionById = new Map(questions.map((q) => [q.id, q]));
    let totalScore = 0;

    responses.forEach((response) => {
        const question = questionById.get(response?.scaleQuestionId);
        const score = deriveScoreFromResponse(response, question);
        if (score != null) totalScore += score;
    });

    const maxScore = questions.reduce((sum, q) => {
        const options = q.questionOptionScores || [];
        if (options.length) {
            const maxOptionScore = Math.max(...options.map((opt) => toNumberOrNull(opt?.score) ?? 0), 0);
            return sum + maxOptionScore;
        }
        return sum + 4; // fallback to the maximum value for Zarit
    }, 0);

    let interpretation = 'Sobrecarga intensa.';
    if (totalScore < 46) interpretation = 'No hay sobrecarga.';
    else if (totalScore <= 56) interpretation = 'Sobrecarga moderada.';

    return { totalScore, totalMaxScore: maxScore || 88, interpretation };
};

export const ScaleResultZARIT = ({
    totalScore,
    totalMaxScore = 88,
    interpretation,
    interpretationTitle = 'Interpretación de la Puntuacion',
    interpretationItems = [
        { label: 'Menor de 46 puntos: Sin sobrecarga.' },
        { label: 'Entre 46 y 56 puntos: Sobrecarga moderada.' },
        { label: 'Mayor de 56 puntos: Sobrecarga intensa.' },
    ],
    onComputedResult,
    register,
    scale,
}) => {
    const derived = React.useMemo(() => computeZaritResult(register, scale), [register, scale]);
    React.useEffect(() => {
                // Usamos onComputedResult, que es lo que recibimos por props
                if (derived && typeof onComputedResult === 'function') {
                    onComputedResult(derived); 
                }
            }, [derived, onComputedResult]);
    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? 0;
    const displayTotalMax = toNumberOrNull(totalMaxScore) ?? derived?.totalMaxScore ?? 88;
    const displayInterpretation = interpretation || derived?.interpretation;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                Puntuación Total: {displayTotalScore}/{displayTotalMax}
            </Typography>

            {displayInterpretation && (
                <List style={{ paddingTop: 0, marginBottom: 16 }}>
                    <ListItem disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={<span><strong>Interpretación:</strong> {displayInterpretation}</span>}
                        />
                    </ListItem>
                </List>
            )}

            <Typography variant="subtitle1" style={{ fontWeight: 600, color: '#0d1b2a' }}>
                {interpretationTitle}
            </Typography>

            <List style={{ paddingTop: 8 }}>
                {interpretationItems.map((item, index) => (
                    <ListItem key={item?.label ?? index} disableGutters>
                        <ListItemIcon style={{ minWidth: 24 }}>
                            <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                        </ListItemIcon>
                        <ListItemText
                            primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                            primary={item?.label || ''}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default ScaleResultZARIT;
